import axios, {
  AxiosError,
  AxiosHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenManager } from '@/shared/api/token-manager';
import type { AuthTokens } from '@/entities/auth';

const AUTH_HEADER_KEY = 'Authorization';
const BEARER_PREFIX = 'Bearer ';

const api = axios.create({
  baseURL:
    import.meta.env.VITE_PUBLIC_API_URL ?? 'http://localhost:3001/api/v1',
  timeout: 10000,
});

const refreshClient = axios.create({
  baseURL:
    import.meta.env.VITE_PUBLIC_API_URL ?? 'http://localhost:3001/api/v1',
  timeout: 10000,
});

let refreshPromise: Promise<AuthTokens | null> | null = null;

const refreshTokens = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const tokens = tokenManager.getTokens();
  if (!tokens?.refresh) {
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await refreshClient.post<AuthTokens>(
        '/auth/token/refresh/',
        {
          refresh: tokens.refresh,
        },
      );
      const nextTokens = response.data;
      const tokensWithUser =
        tokens.user && !nextTokens.user
          ? ({ ...nextTokens, user: tokens.user } as AuthTokens)
          : nextTokens;
      tokenManager.setTokens(tokensWithUser);
      return tokensWithUser;
    } catch (error) {
      tokenManager.clearTokens();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

api.interceptors.request.use((config) => {
  const tokens = tokenManager.getTokens();

  if (!tokens?.access) {
    return config;
  }

  if (config.headers && 'set' in config.headers) {
    config.headers.set(AUTH_HEADER_KEY, `${BEARER_PREFIX}${tokens.access}`);
    return config;
  }

  const headers = new AxiosHeaders(config.headers);
  headers.set(AUTH_HEADER_KEY, `${BEARER_PREFIX}${tokens.access}`);
  config.headers = headers;

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { response, config } = error;

    if (!response || !config) {
      return Promise.reject(error);
    }

    const originalRequest = config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/token/refresh/')
    ) {
      originalRequest._retry = true;

      try {
        const newTokens = await refreshTokens();
        if (!newTokens?.access) {
          return Promise.reject(error);
        }

        if (originalRequest.headers && 'set' in originalRequest.headers) {
          originalRequest.headers.set(
            AUTH_HEADER_KEY,
            `${BEARER_PREFIX}${newTokens.access}`,
          );
        } else {
          const headers = new AxiosHeaders(originalRequest.headers);
          headers.set(AUTH_HEADER_KEY, `${BEARER_PREFIX}${newTokens.access}`);
          originalRequest.headers = headers;
        }

        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export { api };
